import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import './Backtest.css';
import './Backtest2.css';
import './GT2.css';
import LivePosition from './LivePosition';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULTS = { tech: 'QQQ', momentum: 'MTUM', def1: 'GLD', def2: 'IEF', cash: 'BIL' };
const SLOTS = [
  { key: 'tech', label: 'Tech / Macro', hint: 'EMA150/EMA50/SMA20 computed here', cls: 'bt2-input-tech' },
  { key: 'momentum', label: 'Momentum', hint: 'SMA20 momentum scoring', cls: 'bt2-input-momentum' },
  { key: 'def1', label: 'Defensive 1', hint: 'Momentum-ranked risk-off', cls: 'bt2-input-defensive' },
  { key: 'def2', label: 'Defensive 2', hint: 'Momentum-ranked risk-off', cls: 'bt2-input-defensive' },
  { key: 'cash', label: 'Cash Hurdle', hint: 'T-bill hurdle (dual momentum)', cls: 'bt2-input-defensive' },
];

const GT2 = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('inception');
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [symbols, setSymbols] = useState({ ...DEFAULTS });
  const [inputs, setInputs] = useState({ ...DEFAULTS });

  const periodOptions = {
    '3y': '3 Years',
    '5y': '5 Years',
    '10y': '10 Years',
    inception: 'Since Inception',
    extended: 'Extended ~19Y',
  };

  const runBacktest = async (force = false, opts = {}) => {
    const p = opts.period ?? period;
    const s = opts.symbols ?? symbols;
    if (Object.values(s).some((v) => !v)) {
      setError('All five symbols are required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 'inception' → MTUM native max (~12.5y). 'extended' → XMMO-spliced ~19y.
      let apiPeriod = p;
      let extendedFlag = false;
      if (p === 'inception') {
        apiPeriod = 'max';
      } else if (p === 'extended') {
        apiPeriod = 'max';
        extendedFlag = true;
      }
      const asOf = new URLSearchParams(window.location.search).get('as_of');
      const asOfQS = asOf ? `&as_of=${asOf}` : '';
      const url = `${API_URL}/api/gt2/run?force=${force}&period=${apiPeriod}` +
        `&tech=${s.tech}&momentum=${s.momentum}&def1=${s.def1}&def2=${s.def2}&cash=${s.cash}` +
        `&hysteresis_pct=0.02&def_hysteresis_pct=0.01&def_score_mode=roc100` +
        `&extended=${extendedFlag}${asOfQS}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setResults(data);
      } else {
        setError(data.message || 'Backtest failed');
      }
    } catch (err) {
      setError('Connection error - please try again');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runBacktest(); }, []); // eslint-disable-line

  const handlePeriodChange = (p) => {
    setPeriod(p);
    runBacktest(false, { period: p });
  };

  const handleApply = () => {
    const s = Object.fromEntries(
      Object.entries(inputs).map(([k, v]) => [k, v.toUpperCase().trim()]),
    );
    setSymbols(s);
    runBacktest(false, { symbols: s });
  };

  const handleReset = () => {
    setInputs({ ...DEFAULTS });
    setSymbols({ ...DEFAULTS });
    runBacktest(false, { symbols: { ...DEFAULTS } });
  };

  const MetricCard = ({ label, value, subValue, color }) => (
    <div className="bt-metric-card">
      <span className="bt-metric-label">{label}</span>
      <span className="bt-metric-value" style={color ? { color } : {}}>{value}</span>
      {subValue && <span className="bt-metric-sub">{subValue}</span>}
    </div>
  );

  const formatCurrency = (val) => `$${val?.toLocaleString() || 0}`;
  const positionTagClass = (sym) => {
    if (!sym) return '';
    const s = sym.toUpperCase();
    if (s === symbols.tech) return 'bt2-tag-tech';
    if (s === symbols.momentum) return 'bt2-tag-momentum';
    if ([symbols.def1, symbols.def2, symbols.cash].includes(s)) return 'bt2-tag-defensive';
    return '';
  };

  return (
    <div className="bt-container" data-testid="gt2-page">
      <div className="bt-header">
        <a href="/explore" className="bt-back-link">← Back to Dashboard</a>
        <h1 className="bt2-title">GT2 — Regime-Conditional Engine</h1>
        <p className="bt-subtitle">Phase 1 · Dynamic Defensive Bucket</p>
        <p className="bt-description">
          Risk-on: hold {symbols.tech} or {symbols.momentum} by 20-day momentum score with a{' '}
          <strong>2.0% hysteresis</strong> band — identical math to the locked /backtest2 engine.
          Risk-off (close &lt; EMA150 or EMA50 &lt; EMA150): rotate within a{' '}
          <strong>momentum-ranked defensive bucket</strong> [{symbols.def1}, {symbols.def2}, {symbols.cash}]
          scored on 100-day rate-of-change. The cash leg is the natural dual-momentum hurdle —
          gold or treasuries must out-trend T-bills to be held. <strong>1.0% intra-bucket friction</strong>,
          T+1 Open execution, 0.15% slippage.
        </p>
      </div>

      <LivePosition signal={results?.current_signal} key={`gt2lp-${Object.values(symbols).join('-')}`} />

      <div className="bt2-symbol-config" data-testid="gt2-symbol-config">
        <div className="gt2-symbol-grid">
          {SLOTS.map((slot) => (
            <div className="bt2-symbol-slot" key={slot.key}>
              <label className="bt2-slot-label">
                {slot.label} <span className="bt2-slot-default">default {DEFAULTS[slot.key]}</span>
              </label>
              <input
                type="text"
                value={inputs[slot.key]}
                onChange={(e) => setInputs({ ...inputs, [slot.key]: e.target.value.toUpperCase() })}
                className={`bt2-symbol-input ${slot.cls}`}
                placeholder={DEFAULTS[slot.key]}
                maxLength={6}
                data-testid={`gt2-input-${slot.key}`}
              />
              <span className="bt2-slot-hint">{slot.hint}</span>
            </div>
          ))}
        </div>
        <div className="bt2-symbol-actions">
          <button onClick={handleApply} className="bt-run-btn" disabled={loading} data-testid="gt2-apply-symbols">
            Apply &amp; Run
          </button>
          <button onClick={handleReset} className="bt2-reset-btn" disabled={loading} data-testid="gt2-reset-symbols">
            Reset to Defaults
          </button>
        </div>
      </div>

      <div className="bt-actions">
        <div className="bt-period-selector" data-testid="gt2-period-selector">
          <span className="bt-selector-label">Period:</span>
          {Object.entries(periodOptions).map(([key, label]) => (
            <button
              key={key}
              onClick={() => handlePeriodChange(key)}
              className={`bt-period-btn ${period === key ? 'active' : ''}`}
              disabled={loading}
              data-testid={`gt2-period-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => runBacktest(true)} disabled={loading} className="bt-run-btn" data-testid="gt2-run-btn">
          {loading ? 'Running...' : 'Re-run Backtest'}
        </button>
      </div>

      {results && !loading && (
        <div className="bt2-params" data-testid="gt2-params">
          <span><strong>EMA150</strong> macro</span>
          <span><strong>EMA50</strong> regime</span>
          <span><strong>SMA20</strong> momentum</span>
          <span><strong>ROC100</strong> defensive</span>
          <span><strong>2.0%</strong> hysteresis</span>
          <span><strong>1.0%</strong> def friction</span>
          <span><strong>0.15%</strong> slippage</span>
          <span><strong>T+1 Open</strong> exec</span>
        </div>
      )}

      {loading && (
        <div className="bt-loading" data-testid="gt2-loading">
          <div className="bt-spinner"></div>
          <p>Running GT2 {symbols.tech}/{symbols.momentum} + [{symbols.def1}/{symbols.def2}/{symbols.cash}]...</p>
          <p className="bt-loading-sub">Initial run may take 60-120 seconds (Alpha Vantage fetch, 6 symbols).</p>
        </div>
      )}

      {error && <div className="bt-error" data-testid="gt2-error">{error}</div>}

      {results && !loading && (
        <>
          <div className="bt-period" data-testid="gt2-period">
            Backtest Period: <strong>{results.backtest_period.start}</strong> to <strong>{results.backtest_period.end}</strong>
            {' '}({results.backtest_period.trading_days} trading days, {results.backtest_period.years} years)
            {' · '}<strong>{results.symbols.tech}</strong> / <strong>{results.symbols.momentum}</strong>
            {' + ['}<strong>{results.symbols.def1}</strong> / <strong>{results.symbols.def2}</strong> / <strong>{results.symbols.cash}</strong>{']'}
          </div>

          <div className="bt-metrics-section">
            <h2>Strategy Performance</h2>
            <div className="bt-metrics-row" data-testid="gt2-strategy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.strategy_metrics.ending_value)} subValue={`from ${formatCurrency(results.strategy_metrics.starting_value)}`} />
              <MetricCard label="Total Return" value={`${results.strategy_metrics.total_return_pct > 0 ? '+' : ''}${results.strategy_metrics.total_return_pct}%`} color={results.strategy_metrics.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.strategy_metrics.cagr}%`} color={results.strategy_metrics.cagr >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="Max Drawdown" value={`-${results.strategy_metrics.max_drawdown}%`} color="#f59e0b" />
              <MetricCard label="Sharpe Ratio" value={results.strategy_metrics.sharpe_ratio} color={results.strategy_metrics.sharpe_ratio >= 1 ? '#22c55e' : '#f59e0b'} />
              <MetricCard label="Total Trades" value={results.strategy_metrics.total_trades} subValue={`${results.strategy_metrics.trades_per_year}/yr · ${results.strategy_metrics.defensive_rotations} defensive`} />
            </div>
          </div>

          <div className="bt-metrics-section benchmark">
            <h2>SPY Buy-and-Hold Benchmark</h2>
            <div className="bt-metrics-row" data-testid="gt2-spy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.spy_benchmark.ending_value)} subValue="from $100,000" />
              <MetricCard label="Total Return" value={`${results.spy_benchmark.total_return_pct > 0 ? '+' : ''}${results.spy_benchmark.total_return_pct}%`} color={results.spy_benchmark.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.spy_benchmark.cagr}%`} />
              <MetricCard label="Max Drawdown" value={`-${results.spy_benchmark.max_drawdown}%`} color="#f59e0b" />
            </div>
          </div>

          {(() => {
            const diff = results.strategy_metrics.total_return_pct - results.spy_benchmark.total_return_pct;
            const outperformed = diff > 0;
            const ddDiff = results.spy_benchmark.max_drawdown - results.strategy_metrics.max_drawdown;
            const ddBetter = ddDiff > 0;
            return (
              <div className={`bt-outperformance ${outperformed ? 'positive' : 'negative'}`} data-testid="gt2-outperformance">
                Strategy {outperformed ? 'outperformed' : 'underperformed'} SPY by{' '}
                <strong>{Math.abs(diff).toFixed(1)} percentage points</strong>
                {' '}{ddBetter ? 'with' : 'but with'}{' '}
                <strong>{Math.abs(ddDiff).toFixed(1)}%</strong>
                {' '}{ddBetter ? 'less' : 'deeper'} drawdown
              </div>
            );
          })()}

          <div className="gt2-allocation" data-testid="gt2-allocation">
            <h2>Time-in-Asset Allocation</h2>
            <div className="gt2-alloc-row">
              {results.allocation.map((a) => (
                <div key={a.symbol} className={`gt2-alloc-chip ${a.bucket}`} data-testid={`gt2-alloc-${a.symbol.toLowerCase()}`}>
                  <span className="gt2-alloc-sym">{a.symbol}</span>
                  <span className="gt2-alloc-pct">{a.pct}%</span>
                  <span className="gt2-alloc-days">{a.days} days · {a.bucket}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bt-chart-section" data-testid="gt2-chart">
            <h2>Equity Curve: GT2 vs SPY Buy-and-Hold</h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={results.equity_curve} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 10 }} interval={Math.floor(results.equity_curve.length / 12)} />
                <YAxis stroke="#94a3b8" tickFormatter={v => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }}
                  formatter={(value, name) => [`${value}%`, name]}
                  labelFormatter={(label) => `Date: ${label}`}
                  itemSorter={(item) => (item.dataKey === 'portfolio_return' ? 0 : 1)}
                />
                <Legend />
                {results.extended_mode && results.splice_date && (
                  <ReferenceLine
                    x={results.splice_date}
                    stroke="#fbbf24"
                    strokeDasharray="4 4"
                    label={{ value: 'MTUM begins', position: 'top', fill: '#fbbf24', fontSize: 11 }}
                  />
                )}
                <Line type="monotone" dataKey="portfolio_return" name="GT2 Strategy" stroke="#06b6d4" strokeWidth={2.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="spy_return" name="SPY Buy & Hold" stroke="#64748b" strokeWidth={1.5} dot={false} strokeDasharray="5 5" connectNulls />
              </LineChart>
            </ResponsiveContainer>
            {results.extended_mode && (
              <div className="nmv-splice-note" data-testid="gt2-splice-note">
                {results.splice_note || 'XMMO used as proxy for MTUM from April 2013 back.'}
              </div>
            )}
          </div>

          <div className="bt-trades-section" data-testid="gt2-trades">
            <div className="bt-trades-header">
              <h2>Position Changes ({results.total_trades_list_count} total)</h2>
              {results.trades.length > 15 && (
                <button onClick={() => setShowAllTrades(!showAllTrades)} className="bt-toggle-btn" data-testid="gt2-toggle-trades">
                  {showAllTrades ? 'Show Last 15' : 'Show All'}
                </button>
              )}
            </div>
            <div className="bt-table-wrapper">
              <table className="bt-trades-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Action</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Exec Price</th>
                    <th>Portfolio Value</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllTrades ? results.trades : results.trades.slice(-15)).map((trade, idx) => (
                    <tr key={idx}>
                      <td>{trade.trade_number}</td>
                      <td>{trade.date}</td>
                      <td>{trade.action}</td>
                      <td><span className={`bt-position-tag ${positionTagClass(trade.from)}`}>{trade.from}</span></td>
                      <td><span className={`bt-position-tag ${positionTagClass(trade.to)}`}>{trade.to}</span></td>
                      <td>${trade.price?.toFixed(2)}</td>
                      <td>${trade.portfolio_value?.toLocaleString()}</td>
                      <td className="bt2-reason">{trade.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GT2;
